import React, { useEffect, useMemo } from "react"
import getValue from "get-value"
import SEO from "@components/seo"
import CrossSells from "@src/components/cross-sells"
import ContentBuilder from "@components/content-builder"
import StampedEmbed from "@components/reviews/stamped-embed"
import { generateProductJsonLd, generateReviewJsonLd } from "@utils/seo"
import { ascSizeSorter, getInitialVariant } from "@utils/product"
import ImageCarousel from "@components/product/gallery"
import Section from "@components/section"
import ProductCopy from "@components/product/copy"
import ServicesInfo from "@components/services-info"
import ProductDetails from "@components/product/details"
import { Helmet } from "react-helmet"
import { useProductStore } from "@src/hooks/useStore"
import { trackEvent } from "@src/services/analytics"

const ProductView = ({ pageContext, location }) => {
  const { setCurrentVariant } = useProductStore(["setCurrentVariant"])

  const { productEntry, sanityDiscountCodes } = pageContext

  const {
    slug: { current: slugCurrent },
    productDescription,
    variantCopy,
    contentBuilder: contentBuilderBlocks,
    shippingConditions,
    productFeatures,
    productOptions,
    crateSize,
    hasServicesAndPoliciesSection,
  } = productEntry

  const initialVariant = useMemo(() => {
    return getInitialVariant(productEntry.variants, location.search)
  }, [location.search])

  useEffect(() => {
    setCurrentVariant(initialVariant, productEntry, sanityDiscountCodes)
  }, [])

  const servicesPoliciesInfo =
    contentBuilderBlocks?.find(
      block => block?._type === "servicesPoliciesInfo"
    ) || getValue(pageContext, "servicesPoliciesInfo")

  const isRevol =
    slugCurrent && slugCurrent.includes("revol-collapsible-dog-crate")
  const isBowl = slugCurrent && slugCurrent.includes("food-and-water-bowl")
  const isClassicHarness =
    slugCurrent && slugCurrent.includes("classic-harness")

  productEntry.variants = productEntry.variants.sort((varA, varB) =>
    ascSizeSorter(varA.size, varB.size)
  )

  useEffect(() => {
    if (
      location?.state?.fromPlpToPdp &&
      (location?.pathname?.includes("revol-collapsible-dog-crate") ||
        location?.pathname?.includes("evolv-dog-crate"))
    ) {
      trackEvent("ctr_plp_to_pdp")
    }
  }, [location?.state?.fromPlpToPdp])

  return (
    <>
      <SEO {...{ document: productEntry }} />
      <ProductSchemaJsonLd {...{ productEntry, initialVariant }} />
      <article>
        <Section innerClassname={"grid grid-cols-12 relative bg-sage"}>
          <div className="col-span-12 md:col-span-7 image-carousel-column">
            <ImageCarousel
              {...{
                productEntry,
                initialImages: initialVariant?.variantImages,
                mainImageClassName: "is-pdp",
                thumbnailImageClassName: "is-pdp",
              }}
            />
          </div>
          <div className="col-span-12 md:col-span-5">
            {/* <StickyElement classList="mb-8"> */}
            <ProductDetails
              {...{
                productEntry,
                initialVariant: initialVariant,
                productOptions,
                isRevol,
                isBowl,
                isClassicHarness,
                crateSizes: crateSize?.sizes,
                quizLink: crateSize?.quizLink,
                fromList: location?.state?.fromList || null,
                sanityDiscountCodes,
              }}
            />
            <ProductCopy
              {...{
                productEntry,
                productDescription,
                shippingConditions,
                productFeatures,
                variantCopy,
                crateSizes: crateSize?.sizes,
                quizLink: crateSize?.quizLink,
              }}
            />
            <div className="mt-5">
              <ServicesInfo {...servicesPoliciesInfo} />
            </div>
          </div>
        </Section>
        {contentBuilderBlocks && (
          <ProductContentBuilder blocks={contentBuilderBlocks} />
        )}
        <CrossSells
          title="You might also like"
          intro="Upgrade your dog’s comfort with our stylish and functional essentials."
          products={productEntry.youMightAlsoLike}
          className="bg-blush p-10 gap-x-10 overflow-x-auto overflow-y-hidden sm:scrolling-touch"
        />
        <div className="container px-8 mx-auto mb-4 md:pt-16">
          <StampedEmbed type="product" productEntries={[productEntry]} />
        </div>
      </article>
    </>
  )
}

export default ProductView

const ProductContentBuilder = ({ blocks }) => {
  const { currentVariant } = useProductStore(["currentVariant"])

  const selectedSize = getValue(currentVariant, "size")
  const selectedFlavor = getValue(currentVariant, "flavor")
  const selectedConfiguration = getValue(currentVariant, "configuration")

  return (
    <ContentBuilder
      blocks={blocks}
      sizeFilter={selectedSize}
      flavorFilter={selectedFlavor}
      configurationFilter={selectedConfiguration}
      showsInPDP={true}
    />
  )
}

const ProductSchemaJsonLd = ({ productEntry, initialVariant }) => {
  return (
    <Helmet>
      <script key="product-schema" type="application/ld+json">
        {JSON.stringify(
          generateProductJsonLd(productEntry, initialVariant),
          null,
          2
        )}
      </script>
      <script key="reviews-schema" type="application/ld+json">
        {JSON.stringify(
          generateReviewJsonLd(productEntry, initialVariant),
          null,
          2
        )}
      </script>
    </Helmet>
  )
}
